if (!window.customElements.get('mr-color-picker')) {
  class MRColorPicker extends HTMLElement {
    static get observedAttributes() {
      return [
        'data-group-tag',
        'data-current-handle',
        'data-current-product-id',
        'data-current-product-url',
        'data-current-color',
        'data-search-url'
      ];
    }

    connectedCallback() {
      this.list = this.querySelector('[data-color-picker-list]');
      this.currentColorLabel = this.querySelector('[data-current-color-label]');

      this.handleClickBound = this.handleClickBound || this.handleClick.bind(this);

      if (!this._eventsBound) {
        this.addEventListener('click', this.handleClickBound);
        this._eventsBound = true;
      }

      this.refresh();
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      if (!this.isConnected) return;

      clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => {
        this.refresh();
      }, 0);
    }

    refresh() {
      this.groupTag = this.dataset.groupTag || '';
      this.currentHandle = this.dataset.currentHandle || '';
      this.currentProductId = this.dataset.currentProductId || '';
      this.currentProductUrl = this.dataset.currentProductUrl || '';
      this.currentColor = this.dataset.currentColor || '';
      this.searchUrl = this.dataset.searchUrl || '/search';

      this.list = this.querySelector('[data-color-picker-list]');
      this.currentColorLabel = this.querySelector('[data-current-color-label]');

      if (!this.list || !this.groupTag) return;

      this.renderLoadingState();
      this.loadGroup();
    }

    renderLoadingState() {
      if (!this.list) return;

      const currentImage = this.querySelector('.mr-color-picker__item img');
      const imageMarkup = currentImage
        ? `<img
             src="${this.escapeHtml(currentImage.getAttribute('src') || '')}"
             alt="${this.escapeHtml(this.currentColor || this.currentHandle || '')}"
             class="mr-color-picker__image"
             loading="lazy"
           >`
        : '';

      this.list.innerHTML = `
        <a
          href="${this.escapeHtml(this.currentProductUrl || '#')}"
          class="mr-color-picker__item is-active is-loading"
          aria-current="true"
          data-color-link
          data-product-handle="${this.escapeHtml(this.currentHandle || '')}"
          aria-label="${this.escapeHtml(this.currentColor || this.currentHandle || '')}"
        >
          ${imageMarkup}
        </a>
      `;

      if (this.currentColorLabel) {
        this.currentColorLabel.textContent = this.currentColor || '';
      }
    }

    async loadGroup() {
      if (!this.groupTag || !this.list) return;

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      try {
        const url = `${this.searchUrl}?q=tag:${encodeURIComponent(this.groupTag)}&type=product&view=mr-color-group&_=${Date.now()}`;
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          signal
        });

        if (!response.ok) {
          console.warn('MR color picker search response not ok');
          return;
        }

        const products = await response.json();
        if (signal.aborted) return;
        if (!Array.isArray(products) || !products.length) return;

        const uniqueProducts = [];
        const seen = new Set();

        for (const item of products) {
          if (!item || !item.handle) continue;
          if (seen.has(item.handle)) continue;
          seen.add(item.handle);
          uniqueProducts.push(item);
        }

        this.render(uniqueProducts);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('MR color picker error:', error);
      }
    }

    render(products) {
      if (!this.list) return;

      this.list.innerHTML = products
        .map((item) => {
          const isActive = item.handle === this.currentHandle;

          return `
            <a
              href="${this.escapeHtml(item.url || '#')}"
              class="mr-color-picker__item ${isActive ? 'is-active' : ''}"
              ${isActive ? 'aria-current="true"' : ''}
              data-color-link
              data-product-handle="${this.escapeHtml(item.handle || '')}"
              aria-label="${this.escapeHtml(item.color || item.title || '')}"
            >
              ${
                item.featured_image
                  ? `<img
                       src="${this.escapeHtml(item.featured_image)}"
                       alt="${this.escapeHtml(item.color || item.title || '')}"
                       class="mr-color-picker__image"
                       loading="lazy"
                     >`
                  : ''
              }
            </a>
          `;
        })
        .join('');

      const activeProduct = products.find((item) => item.handle === this.currentHandle);
      if (this.currentColorLabel && activeProduct) {
        this.currentColorLabel.textContent = activeProduct.color || this.currentColor || '';
      }
    }

    handleClick(event) {
      const link = event.target.closest('[data-color-link]');
      if (!link || !this.contains(link)) return;

      const href = link.getAttribute('href');
      if (!href) return;

      if (
        href === window.location.pathname ||
        href === window.location.pathname + window.location.search
      ) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      document.documentElement.classList.add('mr-product-transition-out');

      window.setTimeout(() => {
        window.location.href = href;
      }, 140);
    }

    escapeHtml(str) {
      return String(str || '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  window.customElements.define('mr-color-picker', MRColorPicker);
}